define('drops/chart/chart',[
    'jquery',
    'when',
    'underscore',
    'lq/drop',
    'lq/util',
    'lq/cssutil'
], function ($, when, _, Drop, util, cssUtil) {
    'use strict';

    var d3 = null;
    var c3 = null;

    var defaults = {
        data: {
            pageSize: 10240,
            showGridX: false,
            showGridY: false,
            showTooltip: true,
            showLegend: true,
            showDataLabels: false,
            labelType: 'percentage',
            legendPosition: 'bottom',
            showXAxis: true,
            axisXType: 'indexed',
            axisXLabel: '',
            axisXLabelPosition: 'outer-center',
            showYAxis: true,
            axisYLabel: '',
            axisYLabelPosition: 'outer-middle',
            showY2Axis: false,
            axisY2Label: '',
            axisY2LabelPosition: 'outer-middle',
            emptyText: "No data",
            animationDuration: 0,
            removeAxisPadding: false,
            enableZoom: false,
            zoomType: 'scroll',
            zoomBoundaries: false,
            axisXLimit: false,
            enableSubchart: false,
            subchartHeight: 10,
            subchartHide: true,
            scrollbarColor: "#DDDDDD"
        }
    };

    var Chart = function (options) {
        Drop.call(this, $('<div>'), defaults, options);

        this.$chart = $('<div>')
            .appendTo(this.$element);
    };

    Drop.extend(Chart);

    Chart.typeName = 'Chart';

    Chart.prototype.preRender = function () {
        this.piping.visibility.apply(this);

        var self = this;
        this.piping.datasources.registerDatasourceListener(
            this.data.dataSource,
            function (data) { self.update(data); },
            function (data) { self.decorateDataSourceData(data); }
        );

        this.applyPosition();

        var deferred = when.defer();
        require([
                'd3',
                'c3'
            ],
            function (lazyLoadedD3, lazyLoadedC3) {
                cssUtil.requireCss('c3.min.css');

                d3 = lazyLoadedD3;
                c3 = lazyLoadedC3;
                deferred.resolve();
            });

        return deferred.promise;
    };

    Chart.prototype.postRender = function () {
        this.update();
    };

    Chart.prototype.decorateDataSourceData = function (queryData) {
        this.update();
        if (typeof this.data.pageSize === 'number' && this.data.pageSize > 0) {
            queryData.pageSize = this.data.pageSize;
        } else {
            queryData.pageSize = defaults.pageSize;
        }
    };

    Chart.prototype.getStepType = function () {
        var self = this;
        if (self.data.stepType === "at start") return "step-before";
        if (self.data.stepType === "at end") return "step-after";
        return "step";
    };

    Chart.prototype.getTickSettings = function (result, axis) {
        var self = this;
        var precisionSetting = self.data["axis" + axis.toUpperCase() + "Precision"];
        if (_.isFinite(precisionSetting))
            return { format: d3.format('.' + precisionSetting + 'f'), fit: false };


        var seriesSettings = _.findWhere(self.data.series, { axis: axis });
        if (seriesSettings && seriesSettings.output && result.outputs[seriesSettings.output]) {
            var outputType = result.outputs[seriesSettings.output];
            if (outputType === "System.Int32" || outputType === "System.Int64" || outputType === "System.Decimal")
                return { format: util.autoFormatNumber, fit: false };
            else
                return { fit: false }; // c3 replaces category (string) data with indexes if a number format function is passed
        }

        return { format: util.autoFormatNumber, fit: false };
    };

    Chart.prototype.update = function (result) {
        var self = this;

        result = result || {
            outputs: [],
            results: [],
            totalResults: 0
        };

        if (self.chart) {
            self.chart.destroy();
        }

        var series = self.data.series;
        var results = result.results;

        var dynamicPie = _.find(series, function (s) { return s.type === 'pie-dynamic' && s.axis === 'y'; });
        if (dynamicPie) {
            var row = _.reduce(result.results, function (memo, row) {
                memo[row[dynamicPie.labelOutput]] = row[dynamicPie.output];
                return memo;
            }, {});
            results = [row];

            series = _.map(result.results,
                function(row) {
                    var copy = Object.assign({}, dynamicPie);
                    copy.type = 'pie';
                    copy.output = row[dynamicPie.labelOutput];
                    copy.label = row[dynamicPie.labelOutput];
                    return copy;
                });
        }
        
        var seriesWithColorSetting = _.filter(series, function (serie) { return serie.hasCustomColor; });
        var barSeriesWithStack = _.filter(series, function (serie) { return serie.type === "bar-stacked"; });

        var chartSettings = {
            bindto: self.$chart[0],
            transition: {
                duration: self.data.animationDuration
            },
            size: {
                // when there's no anchoring -> set the height explicitly for backwards compatibility reasons
                height: this.data.top != null || this.data.bottom != null ? undefined : self.data.size.y
            },
            // padding
            //color: {
            //    pattern: ['#1f77b4', '#aec7e8', ...]
            //}
            data: {
                names:
                    _.object(
                        _.pluck(series, 'output'), // keys
                        _.pluck(series, 'label')), // values
                types:
                    _.object(
                        _.pluck(series, 'output'),
                        _.map(_.pluck(series, 'type'), function (value) { return value === 'bar-stacked' ? 'bar' : value; })), //bar-stacked doesn't exist => replace it back to bar
                colors:
                    _.object(
                        _.pluck(seriesWithColorSetting, 'output'),
                        _.pluck(seriesWithColorSetting, 'customColor')
                    ),
                axes:
                    _.object(
                        _.pluck(series, 'output'),
                        _.map(series, function (serie) { return serie.axis || 'y'; })),
                groups:
                    [
                        _.pluck(barSeriesWithStack, 'output')
                    ],

                //type: 'line/spline/step/area/area-spline/area-step/bar/scatter/pie/donut/gauge',
                //names: {
                //    upload: 'Upload amount',
                //    download: 'Download amount',
                //}
                //x: 'date', // TODO: change axis.x.type to 'timeseries' when output data type is datetime
                //xs: {
                //    data1: 'x1',
                //    data2: 'x2',
                //},
                //xFormat: '%Y-%m-%d %H:%M:%S' // from d3 https://github.com/mbostock/d3/wiki/Time-Formatting and https://github.com/mbostock/d3/wiki/Formatting
                //classes: {
                //    data1: 'additional-data1-class',
                //    data2: 'additional-data2-class',
                //},
                //groups: [ // for stacked charts
                //    ['data1', 'data2'],
                //    ['data3'],
                //],
                labels: self.data.showDataLabels, // Show labels on each data points.
                //labels: {
                //    format: function (v, id, i, j) { ... }
                //    //v is the value of the data point where the label is shown.
                //    //id is the id of the data where the label is shown.
                //    //i is the index of the data point where the label is shown.
                //    //j is the sub index of the data point where the label is shown.
                //}
                json: results,
                keys: {
                    // x: 'name', // it's possible to specify 'x' when category axis
                    value: _.pluck(series, 'output')
                },
                empty: {
                    label: {
                        text: self.data.emptyText
                    }
                }
            },
            grid: {
                x: {
                    show: self.data.showGridX
                },
                y: {
                    show: self.data.showGridY
                }
            },
            tooltip: {
                show: self.data.showTooltip
            },
            legend: {
                show: self.data.showLegend,
                //inset: {
                //    anchor: 'top-left',
                //    x: 10,
                //    y: 0,
                //    step: undefined
                //},
                position: self.data.legendPosition
            },
            axis: {
                //rotated: true,
                x: {
                    show: self.data.showXAxis,
                    //label: 'Your X Axis',
                    label: {
                        text: self.data.axisXLabel,
                        position: self.data.axisXLabelPosition
                    },
                    tick: self.getTickSettings(result, "x"),
                    min: self.data.axisXLimit ? self.data.axisXLimitMin : undefined,
                    max: self.data.axisXLimit ? self.data.axisXLimitMax : undefined,
                    type: self.data.axisXType,
                    height: self.data.axisXLabelHeight
                    //categories: ['Category 1', 'Category 2'] // if not specified in data
                },
                y: {
                    show: self.data.showYAxis,
                    //inner: false,
                    //inverted: false,
                    label: {
                        text: self.data.axisYLabel,
                        position: self.data.axisYLabelPosition
                    },
                    tick: self.getTickSettings(result, "y")
                },
                y2: {
                    show: self.data.showY2Axis,
                    //inner: false,
                    //inverted: false,
                    label: {
                        text: self.data.axisY2Label,
                        position: self.data.axisY2LabelPosition
                    },
                    tick: self.getTickSettings(result, "y2")
                }
            },
            //point: {
            //    show: false,
            //    r: 5
            //},
            line: {
                step: {
                    type: self.getStepType()
                }
            },
            pie: {
                label: {
                    format: function (value, ratio, id) {
                        if (self.data.labelType === 'percentage') {
                            return (100 * ratio).toFixed(1) + '%';
                        } else {
                            return value;
                        }
                    }
                }
            },
            zoom: {
                enabled: self.data.enableZoom,
                type: self.data.zoomType,
            },
            subchart: {
                show: self.data.enableSubchart,
                // axis: {
                //     x: {
                //         show: false // doesn't work
                //     }
                // },
                size: {
                    height: self.data.subchartHeight
                }
            }
        };

        if (this.isEventEnabled('clicked')) {
            chartSettings.data.onclick = function(chartSerie) {
                self.triggerEventIfEnabled('clicked', chartSerie.id);
            };
        }

        if (self.data.axisYMax || self.data.axisYMax === 0) chartSettings.axis.y.max = self.data.axisYMax;
        if (self.data.axisYMin || self.data.axisYMin === 0) chartSettings.axis.y.min = self.data.axisYMin;

        var xDataSeries = _.findWhere(self.data.series, { axis: 'x' });
        if (xDataSeries) {
            chartSettings.data.x = xDataSeries.output;
            //chartSettings.data.xs =
            //    _.object(
            //        _.pluck(self.data.series, 'output'),
            //        _.map(self.data.series, function (serie) {
            //            return serie.axisXDataOutput || self.data.axisXDataOutput;
            //        }));
        }

        if (self.data.removeAxisPadding) {
            chartSettings.axis.x.padding = { left: 0, right: 0 };
            chartSettings.axis.y.padding = { top: 0, bottom: 0 };
        }

        self.chart = c3.generate(chartSettings);

        // https://github.com/c3js/c3/issues/2357 none of these initial zoom configurations work
        // this workaround isn't perfect either, when zoom type is scroll it has a minor issue
        var xs = self.chart.xs();
        var xArr = xs[Object.keys(xs)[0]];
        if (self.data.enableZoom) {
            if (self.data.zoomBoundaries && (self.data.zoomBoundaryMin || self.data.zoomBoundaryMax) && xArr.length) {
                if (self.data.zoomBoundaryMin && self.data.zoomBoundaryMax) {
                    self.chart.zoom([self.data.zoomBoundaryMin, self.data.zoomBoundaryMax]);
                } else {
                    // var xs = self.chart.xs();
                    // var xArr = xs[Object.keys(xs)[0]];
                    var defaultMin = chartSettings.axis.x.type == "category" ? 0 : Math.min.apply(null, xArr);
                    var defaultMax = chartSettings.axis.x.type == "category" ? xArr.length : Math.max.apply(null, xArr);
                    self.chart.zoom([
                        self.data.zoomBoundaryMin ? Number(self.data.zoomBoundaryMin) : defaultMin,
                        self.data.zoomBoundaryMax ? Number(self.data.zoomBoundaryMax) : defaultMax,
                    ]);
                }
            }
            if (self.data.enableZoom && self.data.enableSubchart) {
                self.$chart.find(".c3-axis-x").not(".c3-axis").hide(); // chartSettings.subchart.axis.x.show = false; doesn't work
                if (self.data.subchartHide) self.$chart.find(".c3-brush > .overlay").attr("fill", self.data.scrollbarColor);
            }
        }

        //THIS CAN BE USED TO ANIMATE TO NEW VALUES:
        /*self.chart.load({
           json: result.results,
                keys: {
                    // x: 'name', // it's possible to specify 'x' when category axis
                    value: _.pluck(self.data.series, 'output')
                }
        });*/

        /*self.chart.selectAll(".percent")
            .on("click", function (d) {
                alert('test');
                // code you want executed on the click event
            });   */
    };

    Chart.prototype.afterShown = function () {
        this.$element.css('display', 'inherit');
    };

    Chart.prototype.afterHidden = function () {
        this.$element.css('display', 'none');
    };

    return Chart;
});

